import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { ActionWrapper } from "@/utils/wretch";
import { zodResolver } from "@hookform/resolvers/zod";
import React from "react";
import { useForm } from "react-hook-form";
import { useFetcher, useNavigate } from "react-router-dom";
import * as z from "zod";
import { DiabolocomInjectionBatch } from "./types";

const agents = [
    { id: "1", name: "Agent 1" },
    { id: "2", name: "Agent 2" },
    { id: "3", name: "Agent 3" }
] as const;

const assignmentTypes = [
    { value: "none", label: "Aucune assignation" },
    { value: "completion_owner", label: "Assignation au CompletionOwner" },
    { value: "manual", label: "Assignation manuelle" }
] as const;

const formSchema = z.object({
    caseIds: z.string().min(1, {
        message: "Veuillez entrer au moins un case_id"
    }),
    assignmentType: z.enum(["none", "completion_owner", "manual"], {
        required_error: "Veuillez sélectionner un type d'assignation"
    }),
    assignedAgent: z.string().nullable(),
    priority: z.number().min(1).max(10)
});

type FormValues = z.infer<typeof formSchema>;

export function ManualInjectionBatchForm() {
    const navigate = useNavigate();
    const fetcher = useFetcher<ActionWrapper<DiabolocomInjectionBatch>>();
    const [showConfirmation, setShowConfirmation] = React.useState(false);
    const [formData, setFormData] = React.useState<FormValues | null>(null);

    const form = useForm<FormValues>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            assignmentType: "none",
            assignedAgent: null,
            priority: 5,
            caseIds: ""
        }
    });

    const assignmentType = form.watch("assignmentType");

    const onSubmit = (data: FormValues) => {
        setFormData(data);
        setShowConfirmation(true);
    };

    const handleConfirm = () => {
        if (!formData) return;

        const caseIdsArray = formData.caseIds
            .split("\n")
            .map(id => id.trim())
            .filter(id => id.length > 0);

        const payload = {
            caseIds: caseIdsArray,
            assign: formData.assignmentType !== "none",
            assignedAgent: formData.assignmentType === "manual" ? formData.assignedAgent : null,
            priority: formData.priority
        };

        fetcher.submit(payload, {
            method: "POST",
            encType: "application/json"
        });
        setShowConfirmation(false);
        navigate("/outils/injection-flash");
    };

    React.useEffect(() => {
        if (fetcher.data?.success && fetcher.data.content?.id) {
            navigate("/outils/injection-flash");
        }
    }, [fetcher.data, navigate]);

    return (
        <>
            <Card>
                <CardHeader>
                    <CardTitle>Nouvelle injection dans la campagne Flash</CardTitle>
                </CardHeader>
                <CardContent>
                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                            <FormField
                                control={form.control}
                                name="caseIds"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Case IDs (un par ligne)</FormLabel>
                                        <FormControl>
                                            <Textarea placeholder="Collez les case_ids ici, un par ligne" className="min-h-[100px]" {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="assignmentType"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Type d'assignation</FormLabel>
                                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                                            <FormControl>
                                                <SelectTrigger>
                                                    <SelectValue placeholder="Sélectionnez un type d'assignation" />
                                                </SelectTrigger>
                                            </FormControl>
                                            <SelectContent>
                                                {assignmentTypes.map(type => (
                                                    <SelectItem key={type.value} value={type.value}>
                                                        {type.label}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            {assignmentType === "manual" && (
                                <FormField
                                    control={form.control}
                                    name="assignedAgent"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Agent assigné</FormLabel>
                                            <Select onValueChange={field.onChange} defaultValue={field.value || undefined}>
                                                <FormControl>
                                                    <SelectTrigger>
                                                        <SelectValue placeholder="Sélectionnez un agent" />
                                                    </SelectTrigger>
                                                </FormControl>
                                                <SelectContent>
                                                    {agents.map(agent => (
                                                        <SelectItem key={agent.id} value={agent.id}>
                                                            {agent.name}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            )}

                            <FormField
                                control={form.control}
                                name="priority"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Priorité d'injection (1 = maximale, 10 = minimale)</FormLabel>
                                        <Select onValueChange={value => field.onChange(parseInt(value))} defaultValue={field.value.toString()}>
                                            <FormControl>
                                                <SelectTrigger>
                                                    <SelectValue placeholder="Sélectionnez une priorité" />
                                                </SelectTrigger>
                                            </FormControl>
                                            <SelectContent>
                                                <SelectItem value="1">1 - Maximale</SelectItem>
                                                <SelectItem value="2">2 - Très élevée</SelectItem>
                                                <SelectItem value="3">3 - Élevée</SelectItem>
                                                <SelectItem value="4">4 - Assez élevée</SelectItem>
                                                <SelectItem value="5">5 - Moyenne</SelectItem>
                                                <SelectItem value="6">6 - Assez moyenne</SelectItem>
                                                <SelectItem value="7">7 - Basse</SelectItem>
                                                <SelectItem value="8">8 - Assez basse</SelectItem>
                                                <SelectItem value="9">9 - Très basse</SelectItem>
                                                <SelectItem value="10">10 - Minimale</SelectItem>
                                            </SelectContent>
                                        </Select>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <Button type="submit">Créer le batch</Button>
                        </form>
                    </Form>
                </CardContent>
            </Card>

            <Dialog open={showConfirmation} onOpenChange={setShowConfirmation}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Confirmer l'injection</DialogTitle>
                        <DialogDescription>Veuillez vérifier les conditions d'injection sélectionnées :</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        {formData && (
                            <>
                                <div>
                                    <strong>Type d'assignation :</strong> {assignmentTypes.find(t => t.value === formData.assignmentType)?.label}
                                </div>
                                {formData.assignmentType === "manual" && formData.assignedAgent && (
                                    <div>
                                        <strong>Agent assigné :</strong> {agents.find(a => a.id === formData.assignedAgent)?.name}
                                    </div>
                                )}
                                <div>
                                    <strong>Priorité :</strong>{" "}
                                    {formData.priority === 1
                                        ? "Maximale (1)"
                                        : formData.priority <= 3
                                          ? "Élevée"
                                          : formData.priority <= 6
                                            ? "Moyenne"
                                            : formData.priority <= 9
                                              ? "Basse"
                                              : "Minimale (10)"}
                                </div>
                                <div>
                                    <strong>Case IDs :</strong>
                                    <ul className="mt-1 list-disc pl-4">
                                        {formData.caseIds
                                            .split("\n")
                                            .filter(id => id.trim().length > 0)
                                            .map((id, index) => (
                                                <li key={index}>{id.trim()}</li>
                                            ))}
                                    </ul>
                                </div>
                            </>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowConfirmation(false)}>
                            Annuler
                        </Button>
                        <Button onClick={handleConfirm}>Confirmer l'injection</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
